import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { CASH_NEMO, CASH_PERCENTAGE, DAP_PORTFOLIO_NAME, DAP_PORTFOLIO_OBJECTIVE, DAP_TICKER } from '@constants/miscellaneous';
import { SellOption } from '@interfaces/movement-flow';
import { IMovementAssetItem } from '@interfaces/movements';
import { IAssetName } from '@interfaces/portfolio-constants';
import { IPortfolioPercentage } from '@interfaces/portfolio-matrix';
import { IPortfolio, IPortfolioTotalAmounts, IDailyInvestmentInfo } from '@interfaces/portfolios';
import { IUser } from '@interfaces/users';
import { ErrorAndLoadingService } from '../error-loading/error-loading.service';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  amountOfPortfolios: number;
  portfoliosSub: any;
  private portfoliosSource = new BehaviorSubject<IPortfolio[]>(null);
  currentPortfoliosSource = this.portfoliosSource.asObservable();
  totalCashExcess: number;

  constructor(private firebaseService: FirebaseService, private errorService: ErrorAndLoadingService) {
    this.calculateTotalCashExcess();
  }

  calculateTotalCashExcess() {
    this.currentPortfoliosSource.subscribe(portfolios => {
      if (portfolios) {
        let totalCashExcess = 0;
        portfolios.forEach(portfolio => {
          const lastHistoricEvolution = portfolio.lastHistoricEvolution;
          if (lastHistoricEvolution && lastHistoricEvolution.totalAmount && !portfolio.isDrivewealth) {
            const assets = lastHistoricEvolution.effectivePortfolioComposition;
            const cashAsset = assets.find((a) => a.assetId === CASH_NEMO);
            const cashExcess = cashAsset.amount - (lastHistoricEvolution.totalAmount * CASH_PERCENTAGE);
            totalCashExcess += Math.max(cashExcess, 0);
          }
        });
        this.totalCashExcess = totalCashExcess;
      }
    });
  }

  getCashExcessFromPortfolio(portfolio: IPortfolio): number {
    if (!portfolio || !portfolio.lastHistoricEvolution ||
        !portfolio.lastHistoricEvolution.effectivePortfolioComposition) {
      return 0;
    }

    const strategicPortfolioComposition = portfolio.strategicPortfolioComposition;
    if (!strategicPortfolioComposition || !strategicPortfolioComposition.portfolio) {
      return 0;
    }
    const strategicCashAsset = strategicPortfolioComposition.portfolio.find((asset) => asset.assetId === CASH_NEMO);
    const strategicCashAssetPercentage = strategicCashAsset ? strategicCashAsset.percentage : 0;
    const strategicCash = portfolio.lastHistoricEvolution.totalAmount * strategicCashAssetPercentage;

    const cashShareVariation = portfolio.lastHistoricEvolution.effectivePortfolioComposition.find(p => p.assetId === CASH_NEMO);
    if (!cashShareVariation) {
      return 0;
    }
    const cashExcess = cashShareVariation.amount - strategicCash;
    const cashExcessInProgress = portfolio.cashExcessInProgress || 0;
    return Math.round(Math.max(cashExcess - cashExcessInProgress, 0));
  }

  private getPortfolioComposition(portfolio: IPortfolio): {
    portfolioComposition: any[],
    currentAssets: any[],
    currentCash: any,
  } {
    const portfolioComposition = [...portfolio.lastHistoricEvolution.effectivePortfolioComposition];
    const currentAssets = portfolioComposition.filter(p => p.assetId !== CASH_NEMO);
    const currentCash = portfolioComposition.find(p => p.assetId === CASH_NEMO);

    return {
      portfolioComposition,
      currentAssets,
      currentCash,
    };
  }

  private getSingleAssetToSell(
    portfolio: IPortfolio,
    amount: number,
    selectedAssetId: string,
    assetNames: IAssetName[],
  ): { assets: IMovementAssetItem[], cashToWithdraw: number } {
    const { currentAssets } = this.getPortfolioComposition(portfolio);
    const selectedAsset = currentAssets.find(p => p.assetId === selectedAssetId);
    const selectedAssetNameInfo = assetNames.find(a => a.assetId === selectedAssetId);

    return {
      assets: [{
        assetId: selectedAssetId,
        name: selectedAssetNameInfo ? selectedAssetNameInfo.assetClass : selectedAssetId,
        amount: amount,
        shares: Math.ceil((amount) / (selectedAsset.sharePrice || 1)),
        sharePrice: selectedAsset.sharePrice,
      }],
      cashToWithdraw: 0,
    };
  }

  private getCashExcessToSell(
    portfolio: IPortfolio,
    amount: number,
    assetNames: IAssetName[],
  ): { assets: IMovementAssetItem[], cashToWithdraw: number } {
    const cashExcess = this.getCashExcessFromPortfolio(portfolio);
    const { currentAssets } = this.getPortfolioComposition(portfolio);

    return {
      assets: currentAssets.map(asset => {
        const selectedAssetNameInfo = assetNames.find(a => a.assetId === asset.assetId);
        return {
          assetId: asset.assetId,
          name: selectedAssetNameInfo ? selectedAssetNameInfo.assetClass : asset.assetId,
          amount: 0,
          shares: 0,
          sharePrice: asset.sharePrice,
        };
      }),
      cashToWithdraw: Math.min(amount, cashExcess),
    };
  }

  private getProportionalAssetsToSell(
    portfolio: IPortfolio,
    amount: number,
    assetNames: IAssetName[],
  ): { assets: IMovementAssetItem[], cashToWithdraw: number } {
    const { currentAssets, currentCash } = this.getPortfolioComposition(portfolio);

    const withdrawableAmount = portfolio.lastHistoricEvolution.totalAmount -
      ((portfolio.withdrawalProgressAmount || 0) - (portfolio.withdrawalCashExcessInProgress || 0)) -
      (portfolio.cashExcessInProgress || 0);

    const nextAmount = withdrawableAmount - amount;
    const cashInProgress = portfolio.cashExcessInProgress || 0;
    const cashWithdrawalsInProgress = portfolio.withdrawalCashInProgress || 0;

    const strategicCashAsset = portfolio.strategicPortfolioComposition.portfolio.find(
      (asset) => asset.assetId === CASH_NEMO,
    ) || { percentage: CASH_PERCENTAGE } as IPortfolioPercentage;

    const cashToLeave = nextAmount * strategicCashAsset.percentage;
    const maxCashAmountToWithdraw = Math.max(
      (currentCash.amount - cashWithdrawalsInProgress) - cashToLeave,
      0,
    );

    const cashExcess = currentCash.amount -
      portfolio.lastHistoricEvolution.totalAmount * strategicCashAsset.percentage;

    const portfolioCashExcess = Math.round(Math.max(cashExcess, 0));
    const cashExcessToSubtract = Math.max(portfolioCashExcess - cashInProgress, 0);
    let amountToDistribute = Math.max(amount - cashExcessToSubtract, 0);
    const cashExcessToUse = Math.max(amount - amountToDistribute, 0);

    let cashToWithdraw = cashExcessToUse + amountToDistribute * currentCash.percentage;
    cashToWithdraw = Math.max(
      Math.min(cashToWithdraw, currentCash.amount, maxCashAmountToWithdraw),
      0,
    );

    const totalAssetAmount = currentAssets
      .map(e => e.amount)
      .reduce((a, b) => a + b, 0);

    amountToDistribute = amount - cashToWithdraw;

    const assets = currentAssets.map(e => {
      const selectedAssetNameInfo = assetNames.find(a => a.assetId === e.assetId);
      return {
        assetId: e.assetId,
        name: selectedAssetNameInfo ? selectedAssetNameInfo.assetClass : e.assetId,
        amount: totalAssetAmount === 0 ? 0 : (e.amount / totalAssetAmount) * amountToDistribute,
        shares: totalAssetAmount === 0 ? 0 :
          Math.min(
            Math.ceil(((e.amount / totalAssetAmount) * amountToDistribute) / (e.sharePrice || 1)),
            e.amountOfShares,
          ),
        sharePrice: e.sharePrice,
      };
    });

    return { assets, cashToWithdraw };
  }

  getAssetsToSell(
    portfolio: IPortfolio,
    amount: number,
    assetNames: IAssetName[],
    sellOption?: SellOption | string,
    selectedAssetId?: string,
  ): { assets: IMovementAssetItem[], cashToWithdraw: number } {
    if (sellOption === SellOption.ONE_ASSET && selectedAssetId) {
      return this.getSingleAssetToSell(portfolio, amount, selectedAssetId, assetNames);
    }

    if (sellOption === SellOption.CASH_EXCESS) {
      return this.getCashExcessToSell(portfolio, amount, assetNames);
    }

    return this.getProportionalAssetsToSell(portfolio, amount, assetNames);
  }

  getPortfoliosObs(uid: string) {
    this.portfoliosSub = this.firebaseService.getSnapshotObs(
      'portfolios', 'userId', uid, this.portfoliosSource, null, 'lastHistoricEvolution.totalAmount', 'desc',
    );
  }

  async getPortfolios(uid: string) {
    const collection = this.firebaseService.getCollection('portfolios');
    const documents = await this.firebaseService.getDocumentsWithSingleQuery(collection, 'userId', '==', uid);
    this.amountOfPortfolios = documents.size;
    const portfolios: IPortfolio[] = [];
    documents.docs.map((doc) => {
      portfolios.push({ ...doc.data(), id: doc.id } as IPortfolio);
    });
    return portfolios;
  }

  async getPortfolioNames(uid: string): Promise<{portfolioId: string, portfolioName: string}[]> {
    if (this.portfoliosSource.value) {
      return this.portfoliosSource.value.map(p => {
        return {
          portfolioName: p.portfolioName,
          portfolioId: p.id};
        });
    }
    return this.getPortfolios(uid)
    .then(portfolios => {
      return portfolios.map(portfolio => {
        return {
          portfolioName: portfolio.portfolioName,
          portfolioId: portfolio.id,
        };
      });
    });
  }

  async updatePortfolio(portfolio: IPortfolio, showToast = true) {
    try {
      const portfolioObject = {...portfolio};
      const id = portfolioObject.id;
      delete portfolioObject.id;
      delete portfolioObject.lastHistoricEvolution;
      delete portfolioObject.initialInvestmentAmount;
      const collection = this.firebaseService.getCollection('portfolios');
      const newPortfolio = await this.firebaseService.updateDocumentWithMerge(portfolioObject, collection, id);
      if (showToast) {
        this.errorService.presentToastWithDuration(2000, 'top', `${portfolioObject.portfolioName} actualizado con éxito`, false);
      }
      return newPortfolio;
    } catch (err) {
      this.errorService.presentToastWithDuration(2000, 'top', `Error al actualizar el portafolio, inténtalo nuevamente`, false);
      throw err;
    }
  }

  async createPortfolio(portfolio: IPortfolio, uid?: string) {
    const collection = this.firebaseService.getCollection('portfolios');
    if (uid) {
      await this.firebaseService.deleteDocument(this.firebaseService.getCollection('contributionStrategies'), uid).catch(() => {});
    }
    return this.firebaseService.addDocument(portfolio, collection);
  }

  async getPortfolio(id: string) {
    if (this.portfoliosSource.value) {
      return this.portfoliosSource.value.find(p => p.id === id);
    }
    try {
      const collection = this.firebaseService.getCollection('portfolios');
      const portfolioSnapshot = await this.firebaseService.getDocumentObject(id, collection);
      const portfolio = portfolioSnapshot.data();
      portfolio['id'] = portfolioSnapshot.id;
      return portfolio as IPortfolio;
    } catch (err) {
      // this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener el portafolio, inténtalo nuevamente', false);
      throw err;
    }
  }

  async getHistoricTotalAmountsByPortfolio(id: string) {
    try {
      const collection = this.firebaseService.getCollection('portfolios/' + id + '/historicTotalAmounts/');
      const totalAmountsSnapshot = await this.firebaseService.getAllDocuments(collection);
      const portfolioTotalAmounts = {
        allTime: null,
      } as IPortfolioTotalAmounts;
      totalAmountsSnapshot.docs.map(doc => {
        portfolioTotalAmounts[doc.id] = (doc.data() as IDailyInvestmentInfo);
      });
      return portfolioTotalAmounts;
    } catch (err) {
      // this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener el portafolio, inténtalo nuevamente', false);
      throw err;
    }
  }

  async getHistoricTotalAmountsByUser(uid: string) {
    try {
      const collection = this.firebaseService.getCollection('users/' + uid + '/' + 'historicTotalAmounts/');
      const totalAmountsSnapshot = await this.firebaseService.getAllDocuments(collection);
      const portfoliosTotalAmounts = {
        allTime: null,
      } as IPortfolioTotalAmounts;
      totalAmountsSnapshot.docs.map(doc => {
        portfoliosTotalAmounts[doc.id] = (doc.data() as IDailyInvestmentInfo);
      });
      return portfoliosTotalAmounts;
    } catch (err) {
      // this.errorService.presentToastWithDuration(2000, 'top', 'Error al obtener el portafolio, inténtalo nuevamente', false);
      throw err;
    }
  }

  deletePortfolio(id: string) {
    return this.firebaseService.deleteDocument(this.firebaseService.getCollection('portfolios'), id);
  }

  async savePortfolioChange(
    oldPortfolio: IPortfolio,
    newStrategicPortfolioComposition,
    newInvestmentHorizon,
    newRiskLevel,
    isPersonalizedPortfolio,
  ) {
    const changeObject = {
      userId: oldPortfolio.userId,
      portfolioId: oldPortfolio.id,
      oldStrategicPortfolioComposition: oldPortfolio.strategicPortfolioComposition,
      oldInvestmentHorizon: oldPortfolio.investmentHorizon,
      oldRiskLevel: oldPortfolio.riskLevel,
      newStrategicPortfolioComposition,
      newInvestmentHorizon,
      newRiskLevel,
      isPersonalizedPortfolio,
    };
    const collection = this.firebaseService.getCollection(`portfolios/${oldPortfolio.id}/strategyChanges`);
    const nowString = (new Date()).toLocaleString('es-CL').replace(',', '').split(' ').map(t => t.split('-').reverse().join('-')).join('_');
    return this.firebaseService.addDocumentWithID(changeObject, collection, nowString);
  }

  deleteContributionStrategy(uid: string) {
    return this.firebaseService.deleteDocument(this.firebaseService.getCollection('contributionStrategies'), uid);
  }

  buildNewDapPortfolio(
    userInfo: IUser,
    initialInvestmentAmount: number,
  ) {
    const lastHistoricEvolution = {
      totalAmount: 0,
      allTimeProfitAmount: 0,
      allTimeProfitPercentage: 0,
      monthProfitAmount: 0,
      monthProfitPercentage: 0,
      yearProfitAmount: 0,
      yearProfitPercentage: 0,
    };

    const strategicPortfolioComposition = {
      maxActiveInvestment: 0.622,
      portfolio: [
        { assetId: DAP_TICKER, percentage: (1 - CASH_PERCENTAGE) },
        { assetId: CASH_NEMO, percentage: CASH_PERCENTAGE },
      ],
      returnAmount: 8.1034,
      standardDeviation: 8.2904,
    };

    return {
      portfolioName: DAP_PORTFOLIO_NAME,
      userId: userInfo.uid,
      lastHistoricEvolution,
      strategicPortfolioComposition,
      riskLevel: 1,
      investmentHorizon: 3,
      investmentObjective: DAP_PORTFOLIO_OBJECTIVE.toString(),
      monthlyInvestmentAmount: 0,
      initialInvestmentAmount,
      isPersonalizedPortfolio: false,
    };
  }
}
